import {
    TextField,
    InputAdornment,
    List,
    ListItem,
    IconButton,
    Box,
    Typography,
    Button,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch,
    Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import styles from '../../styles/costCenterList.module.scss';
import { closeViewDialog, openViewDialog, setFilteredCostCenterList } from 'modules/cbre-flow-view-management/ducks/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { getCostCenterList, getCurrentView, getCurrentViewFilteredCostCenterList, getCurrentViewFilteredSiteList, getViewManagementListApiUrl, getViewsInfo } from 'modules/cbre-flow-view-management/ducks/selectors';
import { DIALOG_SUBTYPES, DIALOG_TYPES } from 'modules/cbre-flow-view-management/ducks/constants';
import { ArrowBack } from '@mui/icons-material';
import apiService from 'util/apiService';
import EmptyState from './EmptyState';

interface ListItemData {
    siteName: string;
    description: string;
    id: string;
    costCenterId: string;
}

interface ViewManagementCostCenterListProps {
    selectedClient: any;
}

const ViewManagementCostCenterList: React.FC<ViewManagementCostCenterListProps> = ({ selectedClient }) => {

    const costCenterList = useSelector(getCostCenterList) || [];

    const filteredList: ListItemData[] = useSelector(getCurrentViewFilteredCostCenterList);

    const filteredSiteList = useSelector(getCurrentViewFilteredSiteList);
    const currentView = useSelector(getCurrentView);

    const dispatch = useDispatch();
    const [addedItems, setAddedItems] = useState<Set<string>>(new Set([])); // Initially some items are added
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const listUrl = useSelector(getViewManagementListApiUrl);
    const currentViewInfo = useSelector(getViewsInfo);
    const searchUrl = `${listUrl}&clientCode=${currentViewInfo?.clientCode}&searchFrom=COST_CENTER`;

    const handleClose = () => {
        dispatch(closeViewDialog());
    };

    const handleToggleItem = (itemId: string) => {
        const newAddedItems = new Set(addedItems);
        if (addedItems.has(itemId)) {
            newAddedItems.delete(itemId);
        } else {
            newAddedItems.add(itemId);
        }
        setAddedItems(newAddedItems);
    };

    useEffect(() => {
        // Reset added items when component mounts
        setAddedItems(new Set(filteredList.map((item: any) => item.costCenterId)));
    }, [filteredList]);

    useEffect(() => {
        // Debounce search
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm.trim()) {
                handleSearch(searchTerm);
            } else {
                setIsSearching(false);
                setSearchResults([]);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handleSearch = async (keyword: string) => {
        setSearchLoading(true);
        setIsSearching(true);
        try {
            const response = await apiService.get(`${searchUrl}&search=${encodeURIComponent(keyword)}`);
            if (response.statusCode === 200) {
                const data = response.data?.allCostCenters?.data || [];
                setSearchResults(data);
            } else {
                setSearchResults([]);
            }
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const displayList = isSearching ? searchResults : costCenterList;
    
    // Add sharedSites to each cost center record
    const enrichedDisplayList = useMemo(() => {
        // Group by name to find shared sites
        const groupedByNameDesc = displayList.reduce((acc: Record<string, any[]>, item: any) => {
            const key = `${item.name}`;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(item);
            return acc;
        }, {});

        // Add sharedSites array to each record
        return displayList.map((item: any) => {
            const key = `${item.name}`;
            const sharedSites = groupedByNameDesc[key].map((i: any) => i.siteName).filter((s: any) => Boolean(s) && s !== item.siteName);
            return {
                ...item,
                sharedSites
            };
        });
    }, [displayList]);

    const initialSelectedCostCenterIds = useMemo(
        () => new Set(filteredList.map((item) => item.costCenterId)),
        [filteredList]
    );
    const hasSelectionChanged = useMemo(() => {
        if (addedItems.size !== initialSelectedCostCenterIds.size) {
            return true;
        }

        for (const costCenterId of addedItems) {
            if (!initialSelectedCostCenterIds.has(costCenterId)) {
                return true;
            }
        }

        return false;
    }, [addedItems, initialSelectedCostCenterIds]);

    const siteGroupedItems = useMemo(() => {
        return enrichedDisplayList.reduce((acc: Record<string, any[]>, item: any) => {
            const siteKey = item?.siteName || item?.site || 'Unassigned site';
            if (!acc[siteKey]) {
                acc[siteKey] = [];
            }
            acc[siteKey].push(item);
            return acc;
        }, {});
    }, [enrichedDisplayList]);

    const siteGroups = useMemo<Array<[string, any[]]>>(
        () => Object.entries(siteGroupedItems) as Array<[string, any[]]>,
        [siteGroupedItems]
    );
    const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());

    // Get selected site names from filteredSiteList (sites with access from primaryList)
    const selectedSiteNames = useMemo(() => {
        // Use filteredSiteList to get sites that should be enabled
        // These are the sites from the primaryList section (sites with EDIT or READ_ONLY access)
        const sites = filteredSiteList.filter((site: any) => 
            site.accessLevel === 'EDIT' || site.accessLevel === 'READ_ONLY'
        );
        return new Set(sites.map((s: any) => s.siteName));
    }, [filteredSiteList]);

    // Check if a site should be enabled
    const isSiteEnabled = (siteName: string) => {
        // If no sites are selected, all sites are enabled
        if (selectedSiteNames.size === 0) {
            return true;
        }
        // Otherwise, only selected sites are enabled
        return selectedSiteNames.has(siteName);
    };

    useEffect(() => {
        setExpandedSites((prev) => {
            const availableSites = new Set(siteGroups.map(([siteName]) => siteName));
            return new Set([...prev].filter((siteName) => availableSites.has(siteName)));
        });
    }, [siteGroups]);

    // Expand all accordions when search results are displayed
    useEffect(() => {
        if (isSearching && searchResults.length > 0) {
            const enabledSites = siteGroups
                .map(([siteName]) => siteName)
                .filter((siteName) => isSiteEnabled(siteName));
            setExpandedSites(new Set(enabledSites));
        }
    }, [isSearching, searchResults, siteGroups, selectedSiteNames]);

    // Calculate total available sites
    const totalAvailableSites = useMemo(() => {
        return siteGroups.length;
    }, [siteGroups]);

    // Check if we should show the warning (selected sites < available sites)
    const shouldShowWarning = useMemo(() => {
        return selectedSiteNames.size > 0 && selectedSiteNames.size < totalAvailableSites;
    }, [selectedSiteNames, totalAvailableSites]);

    // Calculate if all enabled sites are expanded
    const enabledSiteCount = useMemo(() => {
        return siteGroups.filter(([siteName]) => isSiteEnabled(siteName)).length;
    }, [siteGroups, selectedSiteNames]);

    const expandedEnabledSiteCount = useMemo(() => {
        return [...expandedSites].filter((siteName) => isSiteEnabled(siteName)).length;
    }, [expandedSites, selectedSiteNames]);

    // Calculate count of cost centers from enabled sites only
    const enabledCostCenterCount = useMemo(() => {
        return siteGroups
            .filter(([siteName]) => isSiteEnabled(siteName))
            .reduce((count, [, items]) => count + items.length, 0);
    }, [siteGroups, selectedSiteNames]);

    const isAllExpanded = enabledSiteCount > 0 && expandedEnabledSiteCount === enabledSiteCount;

    const handleToggleExpandAll = () => {
        if (isAllExpanded) {
            setExpandedSites(new Set());
            return;
        }
        // Only expand enabled sites
        const enabledSites = siteGroups
            .map(([siteName]) => siteName)
            .filter((siteName) => isSiteEnabled(siteName));
        setExpandedSites(new Set(enabledSites));
    };

    const handleToggleSiteAccordion = (siteName: string) => {
        // Don't toggle if site is disabled
        if (!isSiteEnabled(siteName)) {
            return;
        }
        setExpandedSites((prev) => {
            const updated = new Set(prev);
            if (updated.has(siteName)) {
                updated.delete(siteName);
            } else {
                updated.add(siteName);
            }
            return updated;
        });
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    return (
        <Box className={styles.container}>
            <div style={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Box className={styles.viewHeader}>
                    <Box className={styles.titleRow}>
                        <Typography className={styles.title}>
                            <IconButton
                                onClick={() => {
                                    // setIsOpen(false);
                                    dispatch(openViewDialog(DIALOG_TYPES.EDIT, DIALOG_SUBTYPES.EDIT_PRIMARY_LIST as any));
                                }}
                            >
                                <ArrowBack style={{ color: "#1A1A1AA6" }} />
                            </IconButton>
                            Add cost center filter
                        </Typography>
                    </Box>
                    <Box className={styles.breadcrumb}>
                        <Typography className={`${styles.breadcrumbText} cursor-pointer`} onClick={() => {
                            dispatch(openViewDialog(DIALOG_TYPES.EDIT, DIALOG_SUBTYPES.EDIT_PRIMARY_LIST as any));
                        }}>{currentViewInfo?.clientName}</Typography>
                        <ChevronRightIcon className={styles.breadcrumbIcon} />
                        <Typography className={styles.breadcrumbText}>Cost Centers</Typography>
                        <ChevronRightIcon className={styles.breadcrumbIcon} />
                    </Box>
                </Box>

                <Box className={styles.body}>
                    <TextField
                        fullWidth
                        placeholder="Filter by keyword"
                        variant="outlined"
                        className={styles.searchField}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchLoading ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <>
                                            {searchTerm && (
                                                <IconButton
                                                    onClick={handleClearSearch}
                                                    edge="end"
                                                    sx={{ cursor: 'pointer', padding: '4px', marginRight: '8px' }}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            )}
                                            <SearchIcon className={styles.searchIcon} />
                                        </>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#1A1A1A' }}>
                            {enabledCostCenterCount} cost center(s) across {enabledSiteCount} site(s)
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: '#1A1A1A' }}>Expand all</Typography>
                            <Switch checked={isAllExpanded} onChange={handleToggleExpandAll} size="small" />
                        </Box>
                    </Box>
                    {shouldShowWarning && (
                        <Alert
                            severity="warning"
                            sx={{
                                alignItems: 'center',
                                backgroundColor: '#FFF4E8',
                                border: '1px solid #F2A25A',
                                color: '#1A1A1A',
                                '& .MuiAlert-icon': { color: '#F28C28' },
                                '& .MuiAlert-message': { width: '100%' }
                            }}
                        >
                            Only cost centers for the selected site(s) are available. Change site(s) to select others.
                        </Alert>
                    )}

                    {displayList.length === 0 && isSearching ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: 2 }}>
                            <EmptyState text={searchLoading ? "Loading..." : "No results."} />
                        </Box>
                    ) : (
                        <Box className={styles.accordionContainer}>
                            {siteGroups.map(([siteName, items]) => {
                                const isEnabled = isSiteEnabled(siteName);
                                return (
                                    <Accordion
                                        key={siteName}
                                        expanded={expandedSites.has(siteName) && isEnabled}
                                        onChange={() => handleToggleSiteAccordion(siteName)}
                                        className={styles.accordion}
                                        disabled={!isEnabled}
                                        sx={{
                                            opacity: isEnabled ? 1 : 0.5,
                                            pointerEvents: isEnabled ? 'auto' : 'none',
                                            backgroundColor: isEnabled ? 'inherit' : '#f5f5f5'
                                        }}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
                                            <Typography className={styles.accordionTitle}>
                                                {siteName} ({items.length})
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails className={styles.accordionDetails}>
                                            <List className={styles.list}>
                                                {items.map((item: any) => {
                                                    const isAdded = addedItems.has(item.costCenterId);
                                                    const isHovered = hoveredItem === item.costCenterId;

                                                    return (
                                                        <Box key={item.costCenterId} sx={{ pr: 1 }}>
                                                            <ListItem
                                                                className={styles.listItem}
                                                                onMouseEnter={() => setHoveredItem(item.costCenterId)}
                                                                onMouseLeave={() => setHoveredItem(null)}
                                                                onClick={() => handleToggleItem(item.costCenterId)}
                                                            >
                                                                <Box className={styles.itemContent}>
                                                                    <Box className={styles.textContainer}>
                                                                        <Typography className={styles.itemTitle}>
                                                                            {item.displayName || `${item.name} - ${item.description}`}
                                                                        </Typography>
                                                                        <Typography className={styles.itemDescription}>
                                                                            {item.sharedSites?.length > 1
                                                                                ? `Shared: ${item.siteName} and ${item.sharedSites.length} others`
                                                                                : item.sharedSites?.length === 1
                                                                                    ? `Shared: ${item.siteName}, ${item.sharedSites[0]}`
                                                                                    : item.site && item.siteName && item.site !== item.siteName
                                                                                        ? `Site: ${item.siteName || item.site}`
                                                                                        : ''}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                {isAdded ? (
                                                                    <RemoveCircleOutlineIcon className={styles.editIcon} />
                                                                ) : (
                                                                    isHovered && (
                                                                        <AddCircleIcon className={styles.editIcon} />
                                                                    )
                                                                )}
                                                            </ListItem>
                                                        </Box>
                                                    );
                                                })}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </div>

            <Box className={styles.footer}>
                <Typography variant="body2" className='m-0'>
                    {addedItems.size} selected
                </Typography>

                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!hasSelectionChanged || (isSearching && displayList.length === 0)}
                        onClick={() => {
                            // Save selected items to filteredSiteList
                            const selectedCostCenters = Array.from(addedItems).map(costCenterId => {
                                const item = enrichedDisplayList.find((i: { costCenterId: string; }) => i.costCenterId === costCenterId);
                                return item ? { ...item } : null;
                            }).filter(item => item !== null);

                            dispatch(setFilteredCostCenterList(currentView, selectedCostCenters));

                            // Navigate back to primary list page
                            dispatch(openViewDialog(DIALOG_TYPES.EDIT, DIALOG_SUBTYPES.EDIT_PRIMARY_LIST as any));
                        }}
                    >
                        Update
                    </Button>
                </div>
            </Box>
        </Box>
    );
};

export default ViewManagementCostCenterList;
